import { useState, useEffect } from 'react';
import {
  Truck,
  Search,
  MapPin,
  Calendar,
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  Ship,
  Plane,
  Download
} from 'lucide-react';
import '../styles/EnTransito.css';

const EnTransito = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('todos');
  const [themeColor, setThemeColor] = useState('#c4fffc');

  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  const envios = [
    {
      id: 'ENV-2025-001',
      tracking: 'DHL-789456123',
      ordenCompra: 'OC-2025-004',
      ordenVenta: 'OV-2025-004',
      proveedor: 'MSA Chile',
      cliente: 'Anglo American',
      origen: 'Santiago, Chile',
      destino: 'Antofagasta, Chile',
      transportista: 'DHL Express',
      modo: 'Aéreo',
      fechaEnvio: '2025-01-12',
      fechaEstimada: '2025-01-25',
      estado: 'En Tránsito',
      progreso: 65,
      descripcion: 'EPP certificado para altura',
      urgente: false
    },
    {
      id: 'ENV-2025-002',
      tracking: 'FED-456789321',
      ordenCompra: 'OC-2025-002',
      ordenVenta: 'OV-2025-002',
      proveedor: 'Komatsu Chile',
      cliente: 'Minera Escondida',
      origen: 'Valparaíso, Chile',
      destino: 'Calama, Chile',
      transportista: 'Fedex',
      modo: 'Terrestre',
      fechaEnvio: '2025-01-14',
      fechaEstimada: '2025-02-05',
      estado: 'En Aduana',
      progreso: 30,
      descripcion: 'Válvulas alta presión WEIR 3000PSI',
      urgente: true
    },
    {
      id: 'ENV-2025-003',
      tracking: 'MAE-321654987',
      ordenCompra: 'OC-2025-005',
      ordenVenta: 'OV-2025-005',
      proveedor: 'Fenner Dunlop',
      cliente: 'Antofagasta Minerals',
      origen: 'Houston, USA',
      destino: 'Antofagasta, Chile',
      transportista: 'Maersk',
      modo: 'Marítimo',
      fechaEnvio: '2025-01-10',
      fechaEstimada: '2025-02-15',
      estado: 'En Puerto',
      progreso: 85,
      descripcion: 'Correas transportadoras 72"',
      urgente: false
    },
    {
      id: 'ENV-2025-004',
      tracking: 'UPS-654321789',
      ordenCompra: 'OC-2025-001',
      ordenVenta: 'OV-2025-001',
      proveedor: 'WEIR Chile',
      cliente: 'BHP Billiton',
      origen: 'Lima, Perú',
      destino: 'Iquique, Chile',
      transportista: 'UPS',
      modo: 'Aéreo',
      fechaEnvio: '2025-01-14',
      fechaEstimada: '2025-02-10',
      estado: 'Preparando Despacho',
      progreso: 10,
      descripcion: 'Sistema de filtrado para planta',
      urgente: true
    },
    {
      id: 'ENV-2025-005',
      tracking: 'CHL-987654321',
      ordenCompra: 'OC-2025-006',
      ordenVenta: '',
      proveedor: 'SKF Chile',
      cliente: 'Stock Interno',
      origen: 'Buenos Aires, Argentina',
      destino: 'Santiago, Chile',
      transportista: 'Chilexpress',
      modo: 'Terrestre',
      fechaEnvio: '2025-01-09',
      fechaEstimada: '2025-01-20',
      estado: 'Entregado',
      progreso: 100,
      descripcion: 'Rodamientos industriales varios',
      urgente: false
    }
  ];

  const filteredEnvios = envios.filter(envio => {
    const matchesSearch =
      envio.tracking.toLowerCase().includes(searchTerm.toLowerCase()) ||
      envio.cliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
      envio.proveedor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      envio.descripcion.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterMode === 'todos' ||
      (filterMode === 'urgentes' && envio.urgente) ||
      (filterMode === 'aereo' && envio.modo === 'Aéreo') ||
      (filterMode === 'maritimo' && envio.modo === 'Marítimo') ||
      (filterMode === 'terrestre' && envio.modo === 'Terrestre');

    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (estado) => {
    switch(estado.toLowerCase()) {
      case 'entregado': return 'status-entregado';
      case 'en tránsito': return 'status-transito';
      case 'en aduana': return 'status-aduana';
      case 'en puerto': return 'status-puerto';
      case 'preparando despacho': return 'status-preparando';
      default: return '';
    }
  };

  const getModoIcon = (modo) => {
    switch(modo) {
      case 'Aéreo': return <Plane size={16} />;
      case 'Marítimo': return <Ship size={16} />;
      case 'Terrestre': return <Truck size={16} />;
      default: return <Package size={16} />;
    }
  };

  const getProgressColor = (progreso) => {
    if (progreso === 100) return themeColor;
    if (progreso >= 75) return '#A5D6A7';
    if (progreso >= 50) return '#FFE082';
    if (progreso >= 25) return '#FFCC80';
    return '#FFAB91';
  };

  return (
    <div className="transito-container" style={{ backgroundColor: themeColor }}>
      <div className="transito-header">
        <div className="header-title">
          <Truck size={28} />
          <h1>En Tránsito</h1>
        </div>
        <div className="header-stats">
          <div className="mini-stat">
            <Package size={18} />
            <span>{envios.filter(e => e.estado !== 'Entregado').length} Envíos Activos</span>
          </div>
          <div className="mini-stat urgent" style={{
            backgroundColor: '#000000',
            color: themeColor,
            borderColor: themeColor
          }}>
            <AlertCircle size={18} />
            <span>{envios.filter(e => e.urgente).length} Urgentes</span>
          </div>
        </div>
      </div>

      <div className="transito-controls">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por tracking, cliente, proveedor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="controls-right">
          <select
            className="filter-select"
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="urgentes">Urgentes</option>
            <option value="aereo">Aéreo</option>
            <option value="maritimo">Marítimo</option>
            <option value="terrestre">Terrestre</option>
          </select>
          <button className="btn-export" style={{
            backgroundColor: themeColor,
            color: '#000',
            border: '2px solid #000'
          }}>
            <Download size={20} />
            Exportar
          </button>
        </div>
      </div>

      <div className="envios-grid">
        {filteredEnvios.map((envio) => (
          <div key={envio.id} className={`envio-card ${envio.urgente ? 'urgente' : ''}`} style={{
            boxShadow: envio.urgente ? `0 0 0 2px ${themeColor}` : undefined
          }}>
            {envio.urgente && (
              <div className="urgente-badge" style={{
                backgroundColor: '#000000',
                color: themeColor,
                position: 'absolute',
                top: '16px',
                right: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '700',
                fontFamily: "'Space Grotesk', sans-serif",
                zIndex: 10
              }}>
                <AlertCircle size={16} />
                URGENTE
              </div>
            )}

            <div className="envio-header" style={{ paddingRight: envio.urgente ? '100px' : '0' }}>
              <div className="tracking-info">
                <h3>{envio.tracking}</h3>
                <span className="envio-id">{envio.id}</span>
              </div>
              <div className={`modo-badge ${envio.modo.toLowerCase()}`} style={{
                backgroundColor: envio.modo === 'Aéreo' ? themeColor : envio.modo === 'Marítimo' ? '#ffffff' : '#f0f0f0',
                color: '#000000',
                border: '2px solid #000000'
              }}>
                {getModoIcon(envio.modo)}
                {envio.modo}
              </div>
            </div>

            <div className="envio-route">
              <div className="route-point">
                <MapPin size={16} />
                <div>
                  <span className="label">Origen</span>
                  <p>{envio.origen}</p>
                </div>
              </div>
              <div className="route-arrow">→</div>
              <div className="route-point">
                <MapPin size={16} />
                <div>
                  <span className="label">Destino</span>
                  <p>{envio.destino}</p>
                </div>
              </div>
            </div>

            <div className="envio-details">
              <div className="detail-row">
                <span className="label">Cliente:</span>
                <span className="value">{envio.cliente}</span>
              </div>
              <div className="detail-row">
                <span className="label">Proveedor:</span>
                <span className="value">{envio.proveedor}</span>
              </div>
              <div className="detail-row">
                <span className="label">Transportista:</span>
                <span className="value">{envio.transportista}</span>
              </div>
              <div className="detail-row">
                <span className="label">Descripción:</span>
                <span className="value" title={envio.descripcion}>
                  {envio.descripcion.length > 30
                    ? `${envio.descripcion.substring(0, 30)}...`
                    : envio.descripcion}
                </span>
              </div>
            </div>

            <div className="envio-dates">
              <div className="date-item">
                <Calendar size={14} />
                <span>Envío: {envio.fechaEnvio}</span>
              </div>
              <div className="date-item">
                <Clock size={14} />
                <span>Est. llegada: {envio.fechaEstimada}</span>
              </div>
            </div>

            <div className="envio-progress">
              <div className="progress-header">
                <span className={`status ${getStatusColor(envio.estado)}`} style={{
                  backgroundColor: envio.estado === 'Entregado' ? themeColor :
                                  envio.estado === 'En Aduana' ? '#ffffff' :
                                  envio.estado === 'En Puerto' ? '#000000' :
                                  envio.estado === 'En Tránsito' ? '#f0f0f0' : '#E0E0E0',
                  color: envio.estado === 'En Puerto' ? themeColor : '#000000',
                  border: envio.estado === 'Entregado' ? '2px solid #000000' :
                          envio.estado === 'En Aduana' ? `2px solid ${themeColor}` :
                          envio.estado === 'En Puerto' ? `2px solid ${themeColor}` :
                          envio.estado === 'En Tránsito' ? '2px solid #000000' : '1px solid #000000'
                }}>
                  {envio.estado}
                </span>
                <span className="progress-text">{envio.progreso}%</span>
              </div>
              <div className="progress-bar" style={{
                height: '8px',
                background: '#E0E0E0',
                borderRadius: '4px',
                overflow: 'hidden',
                border: '2px solid #000'
              }}>
                <div
                  className="progress-fill"
                  style={{
                    width: `${envio.progreso}%`,
                    backgroundColor: getProgressColor(envio.progreso),
                    height: '100%'
                  }}
                />
              </div>
            </div>

            <div className="envio-actions">
              <button className="btn-track" style={{
                backgroundColor: '#000000',
                color: themeColor,
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>Ver Tracking</button>
              <div className="ref-links">
                {envio.ordenVenta && <span className="ref" style={{
                  fontSize: '12px',
                  color: '#000000',
                  fontWeight: '700',
                  fontFamily: "'Space Grotesk', sans-serif",
                  padding: '4px 8px',
                  backgroundColor: themeColor,
                  borderRadius: '4px',
                  border: '1px solid #000'
                }}>OV: {envio.ordenVenta}</span>}
                <span className="ref" style={{
                  fontSize: '12px',
                  color: '#000000',
                  fontWeight: '700',
                  fontFamily: "'Space Grotesk', sans-serif",
                  padding: '4px 8px',
                  backgroundColor: themeColor,
                  borderRadius: '4px',
                  border: '1px solid #000'
                }}>OC: {envio.ordenCompra}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EnTransito;